import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
type Option = {
  label: string;
  note: string;
};
type Props = {
  id: string;
  label: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;

/** Boxed dropdown for the enquiry type — closed it reads like the other fields. */
export function TopicSelect({
  id,
  label,
  options,
  value,
  onChange
}: Props) {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(() => Math.max(0, options.findIndex(o => o.label === value)));
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const current = options.find(o => o.label === value) ?? options[0];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);
  useEffect(() => {
    if (open) {
      setActive(Math.max(0, options.findIndex(o => o.label === value)));
      listRef.current?.focus();
    }
  }, [open, options, value]);
  const choose = (i: number) => {
    onChange(options[i].label);
    setOpen(false);
    buttonRef.current?.focus();
  };
  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => (a + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => (a - 1 + options.length) % options.length);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(options.length - 1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      choose(active);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      if (e.key === 'Escape') e.preventDefault();
      setOpen(false);
      if (e.key === 'Escape') buttonRef.current?.focus();
    }
  };
  const onButtonKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
    }
  };
  return <div ref={rootRef} className="relative" data-id="element-426" data-mp="2cm1g3dxa8mre">
      <span id={`${id}-label`} className="sr-only" data-id="element-427" data-mp="2heksw8o5bdv5">
        {label}
      </span>
      <button ref={buttonRef} id={id} type="button" aria-haspopup="listbox" aria-expanded={open} aria-labelledby={`${id}-label ${id}`} onClick={() => setOpen(o => !o)} onKeyDown={onButtonKey} className="flex h-[30px] w-full items-center justify-between gap-4 text-left focus:outline-none focus-visible:underline" data-id="element-428" data-mp="2hgov8m6ec4tb">
        <span className="flex min-w-0 items-baseline gap-3" data-id="element-429" data-mp="jodycqkf3mgj">
          <span className="text-[16px] text-cream" data-id="element-430" data-mp="eqw0bv7xurnb">{current.label}</span>
          <span className="truncate text-[13px] text-cream/55" data-id="element-431" data-mp="apk8rk5i7wch">{current.note}</span>
        </span>
        <ChevronDownIcon aria-hidden="true" strokeWidth={1.25} className={`h-4 w-4 shrink-0 text-cream/70 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] ${open ? 'rotate-180' : ''}`} data-id="element-432" data-mp="5rilen11pav7" />
      </button>

      <AnimatePresence data-id="element-433" data-mp="yhr5wlj207cy">
        {open && <motion.ul ref={listRef} role="listbox" tabIndex={-1} aria-labelledby={`${id}-label`} aria-activedescendant={`${id}-opt-${active}`} onKeyDown={onListKey} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.18,
        ease: EASE
      }} style={{
        transformOrigin: 'top'
      }} className="absolute -left-4 -right-4 top-[calc(100%+17px)] z-30 border border-cream/[0.14] bg-[#18263f] shadow-[0_18px_40px_-20px_rgba(0,0,0,0.55)] focus:outline-none md:-left-8 md:-right-8" data-id="element-434" data-mp="to4sgr52fzg3">
            {options.map((o, i) => {
          const selected = o.label === value;
          return <li key={o.label} id={`${id}-opt-${i}`} role="option" aria-selected={selected} onPointerEnter={() => setActive(i)} onClick={() => choose(i)} className={`flex cursor-pointer items-center justify-between gap-4 px-4 py-3.5 transition-colors duration-150 md:px-8 ${i > 0 ? 'border-t border-cream/[0.1]' : ''} ${active === i ? 'bg-cream/[0.05]' : ''}`} data-id="element-435" data-mp="1op3t5i1hfeik" data-mpi={i}>
                  <span className="flex min-w-0 items-center gap-3" data-id="element-436" data-mp="2hpk3ptzbqd9n">
                    <span aria-hidden="true" className={`inline-block h-[6px] w-[6px] shrink-0 transition-colors duration-150 ${selected ? 'bg-white' : 'border border-cream/40'}`} data-id="element-437" data-mp="2doqg71psn0ha" />
                    <span className="font-accent text-[11px] uppercase tracking-[0.08em] text-cream" data-id="element-438" data-mp="2mk6dxtto5zs5">{o.label}</span>
                    <span className="truncate text-[13px] text-cream/60" data-id="element-439" data-mp="1uqxeg2qwowlh">{o.note}</span>
                  </span>
                  {selected && <CheckIcon aria-hidden="true" strokeWidth={1.25} className="h-4 w-4 shrink-0 text-cream" data-id="element-440" data-mp="28qyoa7p5fed3" />}
                </li>;
        })}
          </motion.ul>}
      </AnimatePresence>
    </div>;
}